import React from 'react';

/**
 * Insider Threat & Data-Exfiltration Spotlight
 *
 * Surfaces the top 5 users whose combined behavioural signals (bulk file
 * downloads, mass deletes, after-hours activity, OAuth/app consents) suggest
 * either insider-threat or compromised-account activity. Each user gets a
 * 0–100 score derived server-side; this component is purely presentational.
 *
 * Why this matters: most SOC reports surface failed sign-ins and IP-blocked
 * malicious activity (incoming threats). Insider-threat indicators look at
 * the activity of *legitimate* accounts and are the leading source of
 * data-loss incidents - they belong in any executive-grade report.
 */

export type InsiderSignalType = 'downloads' | 'deletes' | 'afterHours' | 'oauth';

export interface InsiderSignal {
  type: InsiderSignalType;
  label: string;
  severity: 'high' | 'medium' | 'low';
}

export interface InsiderThreatUser {
  user: string;
  score: number;
  level: 'high' | 'medium' | 'low';
  signals: InsiderSignal[];
  topSignal: string;
}

interface Props {
  users: InsiderThreatUser[];
  /** Period label for the header subtitle, e.g. "this week", "this month". */
  periodLabel: string;
  /** Per-source query failure flags so the UI can explain empty data honestly. */
  errors?: {
    downloads?: boolean;
    deletes?: boolean;
    afterHours?: boolean;
    oauth?: boolean;
  };
}

const SIGNAL_META: Record<InsiderSignalType, { label: string; icon: string; color: string }> = {
  deletes:    { label: 'Mass deletes',     icon: '🗑',  color: '#dc3545' },
  oauth:      { label: 'OAuth / consents', icon: '🔑',  color: '#6f42c1' },
  downloads:  { label: 'Bulk downloads',   icon: '⬇',  color: '#fd7e14' },
  afterHours: { label: 'After-hours',      icon: '🌙',  color: '#17a2b8' },
};

const SEV_COLOR: Record<InsiderSignal['severity'], string> = {
  high:   '#dc3545',
  medium: '#fd7e14',
  low:    '#28a745',
};

const LEVEL_COLOR: Record<InsiderThreatUser['level'], string> = {
  high:   '#dc3545',
  medium: '#fd7e14',
  low:    '#17a2b8',
};

const LEVEL_LABEL: Record<InsiderThreatUser['level'], string> = {
  high:   'High risk',
  medium: 'Medium risk',
  low:    'Watch',
};

function initials(user: string): string {
  const local = user.split('@')[0] ?? user;
  const parts = local.split(/[._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (local.slice(0, 2) || '??').toUpperCase();
}

export function InsiderThreatSpotlight({ users, periodLabel, errors = {} }: Props) {
  const allFailed = errors.downloads && errors.deletes && errors.afterHours && errors.oauth;
  const someFailed = !!(errors.downloads || errors.deletes || errors.afterHours || errors.oauth);
  const safeUsers = Array.isArray(users) ? users : [];

  return (
    <div className="its-root">
      <div className="its-header">
        <div>
          <h3 className="its-title">Insider Threat &amp; Data-Exfiltration Spotlight</h3>
          <p className="its-subtitle">
            Top {Math.max(safeUsers.length, 5)} users by composite UEBA-style signal weight {periodLabel}.
            {' '}Higher score = stronger combination of bulk downloads, mass deletes, after-hours activity, or OAuth consents.
          </p>
        </div>
        <div className="its-legend" aria-hidden="true">
          {(Object.entries(SIGNAL_META) as [InsiderSignalType, typeof SIGNAL_META[InsiderSignalType]][]).map(
            ([key, m]) => (
              <span key={key} className="its-legend-item">
                <span className="its-legend-dot" style={{ background: m.color }} />
                {m.label}
              </span>
            ),
          )}
        </div>
      </div>

      {someFailed && !allFailed && (
        <div className="its-banner" role="status">
          One or more underlying queries failed
          {errors.downloads ? ' (downloads)' : ''}
          {errors.deletes ? ' (deletes)' : ''}
          {errors.afterHours ? ' (after-hours)' : ''}
          {errors.oauth ? ' (oauth)' : ''}
          . Score reflects only the signals that succeeded.
        </div>
      )}

      {allFailed ? (
        <div className="its-empty">
          All four insider-threat queries failed for this workspace. Verify that
          <code> OfficeActivity</code>, <code>SigninLogs</code>, and <code>AuditLogs</code> are flowing
          into the Sentinel workspace.
        </div>
      ) : safeUsers.length === 0 ? (
        <div className="its-empty">
          No insider-threat indicators surfaced for this period. None of the monitored thresholds
          (bulk downloads ≥ 50 files, mass deletes ≥ 25 files, after-hours sign-ins ≥ 50% of activity,
          OAuth consents) were exceeded by any user.
        </div>
      ) : (
        <ul className="its-list" role="list">
          {safeUsers.map((u, idx) => (
            <li
              key={u.user + idx}
              className="its-card"
              aria-label={`${u.user}, risk score ${u.score} of 100, ${LEVEL_LABEL[u.level].toLowerCase()}`}
            >
              <div className="its-card-rank" aria-hidden="true">#{idx + 1}</div>
              <div
                className="its-avatar"
                style={{ background: LEVEL_COLOR[u.level] }}
                aria-hidden="true"
              >
                {initials(u.user)}
              </div>
              <div className="its-card-body">
                <div className="its-card-row1">
                  <span className="its-user" title={u.user}>{u.user}</span>
                  <span
                    className="its-level"
                    style={{ background: LEVEL_COLOR[u.level] }}
                  >
                    {LEVEL_LABEL[u.level]}
                  </span>
                </div>
                <ul className="its-signals" role="list">
                  {u.signals.map((s, i) => {
                    const meta = SIGNAL_META[s.type];
                    return (
                      <li key={i} className="its-signal">
                        <span
                          className="its-signal-icon"
                          style={{ background: meta.color }}
                          aria-hidden="true"
                        >
                          {meta.icon}
                        </span>
                        <span className="its-signal-text">{s.label}</span>
                        <span
                          className="its-signal-sev"
                          style={{ color: SEV_COLOR[s.severity], borderColor: SEV_COLOR[s.severity] }}
                          aria-label={`severity ${s.severity}`}
                        >
                          {s.severity}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="its-score" aria-hidden="true">
                <div className="its-score-num" style={{ color: LEVEL_COLOR[u.level] }}>
                  {u.score}
                </div>
                <div className="its-score-bar">
                  <div
                    className="its-score-bar-fill"
                    style={{
                      width: `${Math.max(4, u.score)}%`,
                      background: LEVEL_COLOR[u.level],
                    }}
                  />
                </div>
                <div className="its-score-label">/ 100</div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="its-footnote">
        Signals derived from <code>OfficeActivity</code> (M365 audit), <code>SigninLogs</code>, and
        {' '}<code>AuditLogs</code>. The list is empty when those tables are absent or no thresholds
        are crossed - it is not a hidden alert queue.
      </p>

      <style>{`
        .its-root { font: inherit; color: #1f2937; }
        .its-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; margin-bottom: 14px; }
        .its-title { margin: 0 0 4px; font-size: 1.05em; font-weight: 600; color: #2c3e50; }
        .its-subtitle { margin: 0; font-size: 12px; color: #475569; max-width: 720px; line-height: 1.5; }
        .its-legend { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; font-size: 11px; color: #475569; }
        .its-legend-item { display: inline-flex; align-items: center; gap: 6px; }
        .its-legend-dot { display: inline-block; width: 10px; height: 10px; border-radius: 999px; }
        .its-banner { background: #fff3cd; color: #856404; border: 1px solid #ffeeba; padding: 8px 12px; border-radius: 8px; font-size: 12px; margin-bottom: 12px; }
        .its-empty { background: #f8fafc; border: 1px dashed #cbd5e1; color: #475569; padding: 18px; border-radius: 10px; font-size: 13px; text-align: center; }
        .its-empty code { background: #e2e8f0; padding: 1px 6px; border-radius: 4px; font-size: 12px; }
        .its-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
        .its-card { display: grid; grid-template-columns: 28px 44px 1fr 110px; align-items: center; gap: 14px; padding: 12px 14px; background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; }
        .its-card-rank { font-weight: 700; color: #94a3b8; font-size: 13px; text-align: center; }
        .its-avatar { width: 44px; height: 44px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 14px; letter-spacing: 0.5px; }
        .its-card-body { min-width: 0; }
        .its-card-row1 { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 6px; }
        .its-user { font-weight: 600; color: #111827; font-size: 14px; word-break: break-all; }
        .its-level { color: #fff; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; padding: 2px 8px; border-radius: 999px; }
        .its-signals { list-style: none; padding: 0; margin: 0; display: grid; gap: 4px; }
        .its-signal { display: flex; align-items: center; gap: 8px; font-size: 12.5px; color: #334155; }
        .its-signal-icon { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 6px; color: #fff; font-size: 12px; flex: 0 0 auto; }
        .its-signal-text { flex: 1 1 auto; min-width: 0; }
        .its-signal-sev { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; padding: 1px 6px; border: 1px solid; border-radius: 4px; flex: 0 0 auto; }
        .its-score { text-align: right; }
        .its-score-num { font-size: 26px; font-weight: 700; line-height: 1; }
        .its-score-label { font-size: 10px; color: #94a3b8; margin-top: 2px; }
        .its-score-bar { height: 4px; background: #e5e7eb; border-radius: 2px; margin-top: 6px; overflow: hidden; }
        .its-score-bar-fill { height: 100%; border-radius: 2px; transition: width 0.3s ease; }
        .its-footnote { margin: 12px 0 0; font-size: 11px; color: #64748b; line-height: 1.5; }
        .its-footnote code { background: #f1f5f9; padding: 1px 5px; border-radius: 3px; font-size: 11px; }
        @media print {
          .its-card { break-inside: avoid; box-shadow: none; }
          .its-list { gap: 8px; }
        }
        @media (max-width: 640px) {
          .its-card { grid-template-columns: 22px 36px 1fr; }
          .its-score { grid-column: 1 / -1; text-align: left; display: flex; align-items: center; gap: 12px; }
          .its-score-num { font-size: 18px; }
          .its-score-bar { flex: 1; margin-top: 0; }
          .its-score-label { margin-top: 0; }
        }
      `}</style>
    </div>
  );
}

export default InsiderThreatSpotlight;
